import { useEffect, useMemo, useRef, useState } from "react";
import {
  ApiError,
  firstUploadRejection,
  type CustomPostAppearance,
  type CustomPostPreview,
  type GroupSummary,
  type MeResponse,
  type PersonalButtonSet,
  type PriceSource,
  type UploadedMedia
} from "../../api/client";
import { ButtonSetPicker } from "../button-sets/ButtonSetPicker";
import { CustomEmojiTextEditor } from "../custom-emoji/CustomEmojiTextEditor";
import { CategorySelectField } from "../price/CategoryPicker";
import { buildCategoryOptions } from "../price/category-model";
import { AppearanceEditor, MediaAttachmentEditor } from "./AppearanceEditor";
import { ButtonLayoutEditor } from "./ButtonLayoutEditor";
import { TelegramPreview } from "./TelegramPreview";
import {
  DEFAULT_CUSTOM_POST_APPEARANCE,
  moveItem
} from "./appearance-model";
import {
  canonicalButtonRows,
  type ButtonLayoutLimits,
  type EditableButtonRow
} from "./button-layout-model";
import { composerSteps, scheduleModesForKind, type ComposerKind, type ScheduleMode } from "./model";

type ComposerApi = {
  loadButtonSets?: () => Promise<PersonalButtonSet[]>;
  loadPrice: () => Promise<PriceSource>;
  uploadMedia?: (file: File) => Promise<UploadedMedia>;
  preview: (body: Record<string, unknown>) => Promise<Record<string, unknown> | CustomPostPreview>;
  createPost: (body: Record<string, unknown>) => Promise<Record<string, unknown>>;
  saveDraft: (body: Record<string, unknown>) => Promise<Record<string, unknown>>;
};

type PreviewResult = Record<string, unknown> | CustomPostPreview;

type PreviewSession = {
  result: PreviewResult;
  payload: Record<string, unknown>;
  mediaItems: UploadedMedia[];
  publishOptions: {
    chatId: number;
    schedule: ScheduleMode;
    dailyTime: string;
    plannedAt: string;
    intervalMinutes: string;
    changeOnly: boolean;
    editOnly: boolean;
  };
};

const revokePreviewUrl = (media: Pick<UploadedMedia, "localPreviewUrl">) => {
  if (media.localPreviewUrl && typeof URL.revokeObjectURL === "function") {
    URL.revokeObjectURL(media.localPreviewUrl);
  }
};

const stepTitles: Record<string, string> = {
  destination: "Куда публикуем",
  products: "Товары и категории",
  price: "Ваша наценка",
  editor: "Текст поста",
  appearance: "Вид публикации",
  schedule: "Когда опубликовать",
  review: "Проверка"
};

const PREVIEW_ERROR_MESSAGE = "Не удалось проверить пост на сервере. Локальный предпросмотр сохранён.";

export function Composer({
  kind,
  groups,
  capabilities,
  buttonLimits,
  api,
  onClose
}: {
  kind: ComposerKind;
  groups: GroupSummary[];
  capabilities: MeResponse["capabilities"];
  buttonLimits: ButtonLayoutLimits;
  api: ComposerApi;
  onClose: () => void;
}) {
  const steps = composerSteps(kind);
  const [stepIndex, setStepIndex] = useState(0);
  const [chatId, setChatId] = useState(String(groups[0]?.chatId ?? ""));
  const [text, setText] = useState("");
  const [categories, setCategories] = useState<string[]>([]);
  const [priceSource, setPriceSource] = useState<PriceSource | null>(null);
  const [priceLoading, setPriceLoading] = useState(kind === "price");
  const [priceError, setPriceError] = useState(false);
  const [priceReloadToken, setPriceReloadToken] = useState(0);
  const [markupType, setMarkupType] = useState<"none" | "fixed" | "percent">("percent");
  const [markupValue, setMarkupValue] = useState("10");
  const [markupRoundMode, setMarkupRoundMode] = useState<"none" | "int" | "hundred_up" | "hundred_down">("none");
  const [title, setTitle] = useState("Прайс на сегодня");
  const [headerParagraph, setHeaderParagraph] = useState("");
  const [contact, setContact] = useState("");
  const [extraInfo, setExtraInfo] = useState("");
  const [promoEnabled, setPromoEnabled] = useState(true);
  const [overflowNoteEnabled, setOverflowNoteEnabled] = useState(true);
  const [priceDeliveryMode, setPriceDeliveryMode] = useState<"legacy" | "rich">("legacy");
  const [buttonRows, setButtonRows] = useState<EditableButtonRow[]>([]);
  const [appearance, setAppearance] = useState<CustomPostAppearance>({
    ...DEFAULT_CUSTOM_POST_APPEARANCE
  });
  const [buttonSets, setButtonSets] = useState<PersonalButtonSet[]>([]);
  const [buttonSetPickerOpen, setButtonSetPickerOpen] = useState(false);
  const [buttonSetStatus, setButtonSetStatus] = useState<string | null>(null);
  const [linkedButtonSet, setLinkedButtonSet] = useState<PersonalButtonSet | null>(null);
  const [mediaItems, setMediaItems] = useState<UploadedMedia[]>([]);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const [mediaBusy, setMediaBusy] = useState(false);
  const mediaInputRef = useRef<HTMLInputElement>(null);
  const [schedule, setSchedule] = useState<ScheduleMode>("now");
  const [dailyTime, setDailyTime] = useState("12:00");
  const [plannedAt, setPlannedAt] = useState("");
  const [intervalMinutes, setIntervalMinutes] = useState("60");
  const [changeOnly, setChangeOnly] = useState(false);
  const [editOnly, setEditOnly] = useState(false);
  const [mirrorToMax, setMirrorToMax] = useState(false);
  const [previewSession, setPreviewSession] = useState<PreviewSession | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const step = steps[stepIndex];
  const manualButtonRows = useMemo(
    () => canonicalButtonRows(buttonRows, buttonLimits),
    [buttonLimits, buttonRows]
  );
  const categoryOptions = useMemo(
    () => priceSource ? buildCategoryOptions(priceSource, categories) : [],
    [categories, priceSource]
  );
  const storedMedia = useMemo(() => mediaItems.map(({
    mediaType,
    mediaFileId,
    fileName,
    contentType,
    size,
    width,
    height,
    duration
  }) => ({
    mediaType,
    mediaFileId,
    fileName,
    contentType,
    size,
    ...(width !== undefined ? { width } : {}),
    ...(height !== undefined ? { height } : {}),
    ...(duration !== undefined ? { duration } : {})
  })), [mediaItems]);
  const draftPayload = useMemo(() => ({
    kind,
    chatId,
    text,
    categories,
    markupType,
    markupValue,
    markupRoundMode,
    title,
    headerParagraph,
    contact,
    extraInfo,
    promoEnabled,
    overflowNoteEnabled,
    priceDeliveryMode,
    buttonRows: manualButtonRows,
    personalButtonSetId: linkedButtonSet?.id ?? null,
    appearance,
    mediaItems: storedMedia,
    schedule,
    dailyTime,
    plannedAt,
    intervalMinutes,
    mirrorToMax: capabilities.max && mirrorToMax
  }), [kind, chatId, text, categories, markupType, markupValue, markupRoundMode, title, headerParagraph, contact, extraInfo, promoEnabled, overflowNoteEnabled, priceDeliveryMode, manualButtonRows, linkedButtonSet, appearance, storedMedia, schedule, dailyTime, plannedAt, intervalMinutes, capabilities.max, mirrorToMax]);
  const mediaItemsRef = useRef<UploadedMedia[]>([]);
  const mountedRef = useRef(true);
  const pendingPreviewUrlsRef = useRef(new Set<string>());
  const previewRequestTokenRef = useRef(0);

  useEffect(() => {
    if (!api.loadButtonSets) return;
    let active = true;
    void api.loadButtonSets().then(
      (sets) => { if (active) setButtonSets(sets); },
      () => { if (active) setButtonSets([]); }
    );
    return () => { active = false; };
  }, [api]);

  useEffect(() => {
    if (kind !== "price") return;
    let active = true;
    setPriceLoading(true);
    setPriceError(false);
    void api.loadPrice().then(
      (source) => {
        if (!active) return;
        setPriceSource(source);
        setPriceLoading(false);
      },
      () => {
        if (!active) return;
        setPriceError(true);
        setPriceLoading(false);
      }
    );
    return () => { active = false; };
  }, [api, kind, priceReloadToken]);

  useEffect(() => {
    mediaItemsRef.current = mediaItems;
  }, [mediaItems]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      previewRequestTokenRef.current += 1;
      mediaItemsRef.current.forEach(revokePreviewUrl);
      pendingPreviewUrlsRef.current.forEach((localPreviewUrl) => {
        revokePreviewUrl({ localPreviewUrl });
      });
      pendingPreviewUrlsRef.current.clear();
    };
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (stepIndex === 0) return;
      void api.saveDraft({ kind, payload: draftPayload }).catch(() => undefined);
    }, 700);
    return () => window.clearTimeout(timer);
  }, [api, draftPayload, kind, stepIndex]);

  const postConfiguration = (nowIso: string) => {
    if (kind === "price") {
      return {
        kind,
        chatId: Number(chatId),
        categories,
        markupType,
        markupValue: Number(markupValue),
        markupRound: markupRoundMode !== "none",
        markupRoundMode,
        title,
        headerParagraph: headerParagraph.trim() || null,
        contact: contact.trim() || null,
        extraInfo: extraInfo.trim() || null,
        promoEnabled,
        overflowNoteEnabled,
        mediaItems: storedMedia,
        buttonRows: manualButtonRows,
        ...(linkedButtonSet ? { personalButtonSetId: linkedButtonSet.id } : {}),
        deliveryMode: priceDeliveryMode,
        scheduleType: schedule,
        intervalMinutes: schedule === "interval" ? Number(intervalMinutes) : undefined,
        dailyTime: schedule === "daily" ? dailyTime : undefined,
        enabled: schedule !== "now",
        postOnChangeOnly: changeOnly,
        editOnChangeOnly: editOnly
      };
    }
    const plannedIso = plannedAt && Number.isFinite(Date.parse(plannedAt))
      ? new Date(plannedAt).toISOString()
      : plannedAt;
    return {
      kind: "custom",
      chatId: Number(chatId),
      text,
      buttonRows: manualButtonRows,
      ...(linkedButtonSet ? { personalButtonSetId: linkedButtonSet.id } : {}),
      mediaItems: storedMedia,
      appearance,
      scheduleType: schedule === "now" ? "planned" : schedule,
      intervalMinutes: schedule === "interval" ? Number(intervalMinutes) : undefined,
      dailyTime: schedule === "now"
        ? nowIso
        : schedule === "planned"
          ? plannedIso
          : schedule === "daily"
            ? dailyTime
            : undefined,
      enabled: true,
      ...(capabilities.admin ? { mirrorToMax: capabilities.max && mirrorToMax } : {})
    };
  };

  const localCustomPreview = useMemo<CustomPostPreview>(() => {
    const keyboard: NonNullable<CustomPostPreview["messages"][number]["keyboard"]> = manualButtonRows.map((row, rowIndex) => row.buttons.map((button) => ({
          text: button.text || "Новая кнопка",
          url: button.url || "https://example.invalid",
          row: rowIndex,
          ...(button.style === "default" ? {} : { style: button.style }),
          ...(button.customEmojiId ? { customEmojiId: button.customEmojiId } : {}),
          ...(button.customEmojiId && button.emojiFallback ? { emojiFallback: button.emojiFallback } : {})
        })));
    const effectiveFormat = appearance.deliveryPreference === "rich" && capabilities.richPosts === true
      ? "rich"
      : "legacy";
    const media = storedMedia.map((item, index) => ({ index, mediaType: item.mediaType }));
    const firstMedia = storedMedia[0]?.mediaType;
    return {
      effectiveFormat,
      overriddenBy: null,
      warnings: [],
      messages: [{
        kind: effectiveFormat === "rich" ? "rich" : firstMedia ?? "text",
        text: text || "Текст публикации",
        ...(media.length ? { media } : {}),
        ...(keyboard.length ? { keyboard } : {}),
        layout: appearance.layout,
        showCaptionAboveMedia: appearance.layout === "text_first",
        buttonAlignment: appearance.buttonAlignment
      }]
    };
  }, [appearance, capabilities.richPosts, manualButtonRows, storedMedia, text]);

  const addMedia = async (files: FileList | null) => {
    if (!files?.length || !api.uploadMedia) return;
    const selected = Array.from(files).slice(0, Math.max(0, 10 - mediaItems.length));
    if (selected.length === 0) {
      setMediaError("Можно добавить не более 10 файлов.");
      return;
    }
    const rejection = firstUploadRejection(selected);
    if (rejection) {
      setMediaError(selected.length > 1
        ? `${rejection} Остальные выбранные файлы тоже не добавлены — выберите их снова без этого файла.`
        : rejection);
      if (mediaInputRef.current) mediaInputRef.current.value = "";
      return;
    }
    setMediaBusy(true);
    setMediaError(null);
    const uploaded: UploadedMedia[] = [];
    try {
      for (const file of selected) {
        const stored = await api.uploadMedia(file);
        if (!mountedRef.current) return;
        let localPreviewUrl: string | undefined;
        if (typeof URL.createObjectURL === "function") {
          try { localPreviewUrl = URL.createObjectURL(file); }
          catch { localPreviewUrl = undefined; }
        }
        if (localPreviewUrl) pendingPreviewUrlsRef.current.add(localPreviewUrl);
        uploaded.push({ ...stored, ...(localPreviewUrl ? { localPreviewUrl } : {}) });
      }
      if (!mountedRef.current) return;
      const nextMediaItems = [...mediaItemsRef.current, ...uploaded];
      mediaItemsRef.current = nextMediaItems;
      setMediaItems(nextMediaItems);
      uploaded.forEach((media) => {
        if (media.localPreviewUrl) pendingPreviewUrlsRef.current.delete(media.localPreviewUrl);
      });
      if (mediaItems.some((media) => media.mediaType === "video") || uploaded.some((media) => media.mediaType === "video")) {
        setAppearance((current) => ({ ...current, richVideoOverrideAck: false }));
      }
    } catch (error) {
      uploaded.forEach((media) => {
        if (
          media.localPreviewUrl &&
          pendingPreviewUrlsRef.current.delete(media.localPreviewUrl)
        ) {
          revokePreviewUrl(media);
        }
      });
      if (mountedRef.current) {
        setMediaError(error instanceof ApiError
          ? error.message
          : "Не удалось загрузить файл. Попробуйте ещё раз.");
      }
    } finally {
      if (mountedRef.current) {
        setMediaBusy(false);
        if (mediaInputRef.current) mediaInputRef.current.value = "";
      }
    }
  };

  const moveMedia = (index: number, offset: number) => {
    setMediaItems((current) => {
      const target = index + offset;
      if (target < 0 || target >= current.length) return current;
      if (current.some((media) => media.mediaType === "video")) {
        setAppearance((value) => ({ ...value, richVideoOverrideAck: false }));
      }
      return moveItem(current, index, target);
    });
  };

  const removeMedia = (index: number) => {
    setMediaItems((current) => {
      const removed = current[index];
      if (!removed) return current;
      revokePreviewUrl(removed);
      if (current.some((media) => media.mediaType === "video")) {
        setAppearance((value) => ({ ...value, richVideoOverrideAck: false }));
      }
      return current.filter((_, itemIndex) => itemIndex !== index);
    });
  };

  const invalidatePreviewSession = () => {
    previewRequestTokenRef.current += 1;
    setPreviewSession(null);
    setPreviewBusy(false);
    setPreviewError(null);
  };

  const changeButtonRows = (rows: EditableButtonRow[]) => {
    invalidatePreviewSession();
    setLinkedButtonSet(null);
    setButtonSetStatus(null);
    setButtonRows(rows);
  };

  const copyButtonSet = (rows: EditableButtonRow[]) => {
    invalidatePreviewSession();
    setLinkedButtonSet(null);
    setButtonRows(rows);
    setButtonSetStatus("Набор скопирован в пост");
    setButtonSetPickerOpen(false);
  };

  const linkButtonSet = (item: PersonalButtonSet) => {
    invalidatePreviewSession();
    setLinkedButtonSet(item);
    setButtonRows(item.buttonRows as EditableButtonRow[]);
    setButtonSetStatus(`Связан с набором «${item.name}»`);
    setButtonSetPickerOpen(false);
  };

  const back = () => {
    invalidatePreviewSession();
    if (stepIndex === 0) onClose();
    else setStepIndex(stepIndex - 1);
  };

  const closeComposer = () => {
    invalidatePreviewSession();
    onClose();
  };

  const requestPreview = async () => {
    const token = previewRequestTokenRef.current + 1;
    previewRequestTokenRef.current = token;
    const payload = JSON.parse(JSON.stringify(postConfiguration(new Date().toISOString()))) as Record<string, unknown>;
    const mediaSnapshot = mediaItems.map((media) => ({ ...media }));
    const publishOptions = {
      chatId: Number(chatId),
      schedule,
      dailyTime,
      plannedAt,
      intervalMinutes,
      changeOnly,
      editOnly
    };
    setPreviewSession(null);
    setPreviewError(null);
    setPreviewBusy(true);
    try {
      const result = await api.preview(payload);
      if (!mountedRef.current || previewRequestTokenRef.current !== token) return;
      setPreviewSession({ result, payload, mediaItems: mediaSnapshot, publishOptions });
      setPreviewError(null);
    } catch {
      if (!mountedRef.current || previewRequestTokenRef.current !== token) return;
      setPreviewSession(null);
      setPreviewError(PREVIEW_ERROR_MESSAGE);
    } finally {
      if (mountedRef.current && previewRequestTokenRef.current === token) {
        setPreviewBusy(false);
      }
    }
  };

  const next = async () => {
    if (step === "review" || previewBusy) return;
    const nextIndex = stepIndex + 1;
    if (steps[nextIndex] === "review") {
      setStepIndex(nextIndex);
      await requestPreview();
      return;
    }
    setStepIndex(nextIndex);
  };

  const publish = async () => {
    if (!previewSession || !previewSession.publishOptions.chatId || kind === "catalog") return;
    setBusy(true);
    const { payload, publishOptions } = previewSession;
    const previewSessionId = "previewSessionId" in previewSession.result &&
      typeof previewSession.result.previewSessionId === "string"
        ? previewSession.result.previewSessionId
        : null;
    try {
      const requestId = crypto.randomUUID();
      await api.createPost({
        ...payload,
        requestId,
        ...(previewSessionId ? { previewSessionId } : {})
      });
      setResult(publishOptions.schedule === "now" ? "Пост поставлен в очередь" : "Пост запланирован");
    } catch { setResult("Не удалось сохранить. Черновик остался в приложении."); }
    finally { setBusy(false); }
  };

  if (result) return <section className="composer result-panel"><div className="state-mark">✓</div><h1>{result}</h1><button className="primary-button" type="button" onClick={closeComposer}>Готово</button></section>;

  return (
    <section className="composer" aria-label="Создание публикации">
      <div className="composer-content">
      <header className="composer-header"><button type="button" onClick={back} aria-label="Назад">←</button><div><p className="eyebrow">Шаг {stepIndex + 1} из {steps.length}</p><h1>{stepTitles[step]}</h1></div><button type="button" onClick={closeComposer} aria-label="Закрыть">×</button></header>
      <div className="progress-track"><span style={{ width: `${((stepIndex + 1) / steps.length) * 100}%` }} /></div>
      <div className="composer-body">
        {step === "destination" && <div className="field-stack"><p className="helper-copy">Доступны только ваши группы, где бот уже видел вас.</p>{groups.length === 0 ? <div className="empty-state">Добавьте бота в группу и напишите там любое сообщение.</div> : groups.map((group) => <label className="choice-row" key={group.chatId}><input type="radio" name="group" value={group.chatId} checked={chatId === String(group.chatId)} onChange={(event) => setChatId(event.target.value)} /><span><strong>{group.title || "Группа"}</strong><small>{group.type} · {group.customEmojiCapability === "supported" ? "анимированные эмоджи" : group.customEmojiCapability === "unsupported" ? "обычные эмоджи" : "эмоджи проверит Telegram"}</small></span></label>)}</div>}
        {step === "products" && <div className="field-stack"><CategorySelectField options={categoryOptions} value={categories} loading={priceLoading} error={priceError} onRetry={() => setPriceReloadToken((token) => token + 1)} onApply={(keys) => { invalidatePreviewSession(); setCategories(keys); }} /><p className="helper-copy">Пустой выбор означает весь прайс.</p></div>}
        {step === "price" && <div className="field-stack"><label className="field"><span>Тип наценки</span><select value={markupType} onChange={(event) => setMarkupType(event.target.value as typeof markupType)}><option value="percent">Процент</option><option value="fixed">Фиксированно</option><option value="none">Без наценки</option></select></label>{markupType !== "none" && <label className="field"><span>Значение</span><input type="number" inputMode="decimal" value={markupValue} onChange={(event) => setMarkupValue(event.target.value)} /></label>}{markupType === "percent" && <label className="field"><span>Округление наценки</span><select aria-label="Округление наценки" value={markupRoundMode} onChange={(event) => setMarkupRoundMode(event.target.value as typeof markupRoundMode)}><option value="none">Не округлять</option><option value="int">До целого</option><option value="hundred_up">Вверх до 100</option><option value="hundred_down">Вниз до 100</option></select></label>}<label className="switch-row"><input type="checkbox" checked={changeOnly} onChange={(event) => setChangeOnly(event.target.checked)} /><span><strong>Только при изменении прайса</strong><small>Не публиковать тот же набор цен</small></span></label><label className="switch-row"><input type="checkbox" checked={editOnly} onChange={(event) => setEditOnly(event.target.checked)} /><span><strong>Авторедактируемый пост</strong><small>Обновлять последнее опубликованное сообщение и будущие публикации</small></span></label></div>}
        {step === "editor" && <div className="field-stack"><CustomEmojiTextEditor label="Текст поста" rows={9} value={text} onChange={setText} placeholder="Что хотите рассказать?" /></div>}
        {step === "appearance" && <div className="field-stack">
          {kind === "price" && <>
            <CustomEmojiTextEditor label="Заголовок поста" rows={2} value={title} onChange={setTitle} />
            <CustomEmojiTextEditor label="Абзац после заголовка" rows={4} value={headerParagraph} onChange={setHeaderParagraph} placeholder="Например: товары в наличии сегодня" />
            <CustomEmojiTextEditor label="Контакт для заказа" rows={2} value={contact} onChange={setContact} placeholder="@manager или телефон" />
            <CustomEmojiTextEditor label="Инфо-блок" rows={4} value={extraInfo} onChange={setExtraInfo} placeholder="Адрес, доставка, часы работы" />
            <label className="switch-row"><input aria-label="Показывать промо-строку" type="checkbox" checked={promoEnabled} onChange={(event) => setPromoEnabled(event.target.checked)} /><span><strong>Показывать промо-строку</strong><small>Стандартная строка из бота</small></span></label>
            <label className="switch-row"><input aria-label="Добавлять приписку про PDF при переполнении" type="checkbox" checked={overflowNoteEnabled} onChange={(event) => setOverflowNoteEnabled(event.target.checked)} /><span><strong>Приписка про PDF при переполнении</strong><small>Показывается, если весь прайс не помещается в сообщение</small></span></label>
            <MediaAttachmentEditor mediaItems={mediaItems} mediaBusy={mediaBusy} mediaError={mediaError} mediaInputRef={mediaInputRef} onAddMedia={(files) => void addMedia(files)} onMoveMedia={moveMedia} onRemoveMedia={removeMedia} />
            <section className="personal-button-set-action"><div><strong>Готовые кнопки</strong><small>Свяжите набор для автоматических обновлений или создайте независимую копию.</small></div><button type="button" onClick={() => setButtonSetPickerOpen(true)}>Выбрать из моих наборов</button>{buttonSetStatus && <p className="save-success" role="status" aria-label="Статус набора кнопок">{buttonSetStatus}</p>}</section>
            <ButtonLayoutEditor rows={buttonRows} onChange={changeButtonRows} richEnabled={capabilities.richPosts === true} limits={buttonLimits} />
            <fieldset className="field-stack"><legend>Как уйдёт в Telegram</legend><label className="choice-row"><input type="radio" name="price-delivery-mode" aria-label="Обычный" checked={priceDeliveryMode === "legacy"} onChange={() => setPriceDeliveryMode("legacy")} /><span><strong>Обычный</strong><small>Надёжный формат по умолчанию</small></span></label><label className="choice-row"><input type="radio" name="price-delivery-mode" aria-label="Новый Rich — бета" checked={priceDeliveryMode === "rich"} disabled={capabilities.richPosts !== true} onChange={() => setPriceDeliveryMode("rich")} /><span><strong>Новый Rich — бета</strong><small>Включается только вручную</small></span></label></fieldset>
          </>}
          {kind === "custom" && <AppearanceEditor capabilities={capabilities} appearance={appearance} onAppearanceChange={setAppearance} buttonRows={buttonRows} onButtonRowsChange={changeButtonRows} buttonLimits={buttonLimits} buttonSetStatus={buttonSetStatus} onChooseButtonSet={() => setButtonSetPickerOpen(true)} mediaItems={mediaItems} mediaBusy={mediaBusy} mediaError={mediaError} mediaInputRef={mediaInputRef} onAddMedia={(files) => void addMedia(files)} onMoveMedia={moveMedia} onRemoveMedia={removeMedia} mirrorToMax={capabilities.max && mirrorToMax} />}
        </div>}
        {step === "schedule" && <div className="field-stack">{scheduleModesForKind(kind).map((option) => <label className="choice-row" key={option.id}><input type="radio" name="schedule" checked={schedule === option.id} onChange={() => setSchedule(option.id)} /><span><strong>{option.label}</strong><small>{option.description}</small></span></label>)}{schedule === "daily" && <label className="field"><span>Время, мск</span><input type="time" value={dailyTime} onChange={(event) => setDailyTime(event.target.value)} /></label>}{schedule === "planned" && <label className="field"><span>Дата и время, мск</span><input type="datetime-local" value={plannedAt} onChange={(event) => setPlannedAt(event.target.value)} /></label>}{schedule === "interval" && <label className="field"><span>Интервал, минут</span><input type="number" min="5" value={intervalMinutes} onChange={(event) => setIntervalMinutes(event.target.value)} /></label>}{capabilities.max && kind === "custom" && <label className="switch-row admin-only"><input type="checkbox" checked={mirrorToMax} onChange={(event) => { const checked = event.target.checked; setMirrorToMax(checked); if (checked && mediaItems.some((media) => media.mediaType === "video")) setAppearance((value) => ({ ...value, richVideoOverrideAck: false })); }} /><span><strong>Переслать в MAX</strong><small>Админский канал</small></span></label>}</div>}
        {step === "review" && <div className="field-stack review-preview-stack">
          <div className="preview-card preview-card-main">
            <p className="eyebrow">{previewSession ? "Предпросмотр Telegram" : "Локальный предпросмотр"}</p>
            {kind === "custom" || (previewSession && "messages" in previewSession.result) ? (
              <TelegramPreview
                display="inline"
                preview={previewSession ? previewSession.result as CustomPostPreview : localCustomPreview}
                mediaItems={previewSession?.mediaItems ?? mediaItems}
              />
            ) : <pre>{previewSession && "text" in previewSession.result ? String(previewSession.result.text) : title}</pre>}
          </div>
          <section className={`server-preview-check ${previewError ? "failed" : previewSession ? "success" : "pending"}`} aria-label="Проверка сервером">
            <strong>Проверка сервером</strong>
            {previewBusy && <><div className="skeleton preview-skeleton" /><p>Проверяем итоговый пост…</p></>}
            {!previewBusy && previewSession && <p role="status">Проверено ✓</p>}
            {!previewBusy && (previewError || !previewSession) && <div className="preview-error"><p role="alert">{previewError ?? PREVIEW_ERROR_MESSAGE}</p><button type="button" onClick={() => void requestPreview()}>Повторить проверку</button></div>}
          </section>
        </div>}
      </div>
      <footer className="composer-footer">{step === "review" ? <button className="primary-button" type="button" disabled={busy || previewBusy || kind === "catalog" || !previewSession} onClick={publish}>{schedule === "now" ? "Опубликовать" : "Включить автопост"}</button> : <button className="primary-button" type="button" disabled={previewBusy || mediaBusy || (step === "destination" && !chatId)} onClick={() => void next()}>Дальше</button>}</footer>
      </div>
      {buttonSetPickerOpen && <ButtonSetPicker sets={buttonSets} currentRows={buttonRows} onLink={linkButtonSet} onCopy={copyButtonSet} onClose={() => setButtonSetPickerOpen(false)} />}
    </section>
  );
}
